<template>
    <div class="website-traffic-by-country-chart" :class="{ fullscreen: fullScreen }">
        <div class="toolbar">
            <div class="left">
                <div class="buttons">
                    <IconButton
                        :classNames="tableMode ? '' : 'selected'"
                        :onClick="() => (tableMode = false)"
                        v-tippy="{ content: $t('report.chartView') }"
                    >
                        <md-icon>insights</md-icon>
                    </IconButton>
                    <IconButton
                        :classNames="tableMode ? 'selected' : ''"
                        :onClick="() => (tableMode = true)"
                        v-tippy="{ content: $t('report.tableView') }"
                    >
                        <md-icon>table_view</md-icon>
                    </IconButton>
                    <IconButton :onClick="exportToExcel" v-tippy="{ content: $t('report.download') }">
                        <md-icon>download</md-icon>
                    </IconButton>
                    <IconButton :onClick="switchToFullScreenMode" v-tippy="{ content: $t('report.fullScreen') }">
                        <md-icon v-if="!fullScreen">fullscreen</md-icon>
                        <md-icon v-else>fullscreen_exit</md-icon>
                    </IconButton>
                </div>
            </div>
        </div>
        <div class="chart-wrapper">
            <EChartsHorizontalBar
                :labels="getLabels()"
                :datasets="getDatasets()"
                :height="320"
                v-if="!tableMode"
            />
            <TableView :cols="getCols()" :rows="getRows()" v-if="tableMode" />
        </div>

        <div class="stats">
            <StatCard :title="$t('report.topVisitorCountry')" :value="formatStat(getStats().TopCountry)" />
        </div>
    </div>
</template>
<script>
import Vue from "vue";
import moment from "moment";
import _ from "lodash";
import IconButton from "../../Pages/Settings/components/IconButton.vue";
import StatCard from "../../Pages/Chatbot/pages/reports/charts/StatCard.vue";
import TableView from "../../Pages/Chatbot/pages/reports/charts/TableView.vue";
import EChartsHorizontalBar from "../../../components/charts/EChartsHorizontalBar.vue";
import { expertToExcel } from "../../../utils/utils";
import log2 from "../../../utils/log2";
import { mapState } from "vuex";
import fscreen from "fscreen";
import { REPORT_API } from "../../../api/statistics";

const log = log2("WebsiteTrafficByCountry");
export default Vue.extend({
    name: "WebsiteTrafficByCountry",
    components: { EChartsHorizontalBar, TableView, IconButton, StatCard },
    props: {
        filters: {
            type: Object,
            default: () => ({
                users: [],
                channels: [],
                startDate: moment().startOf("day").toISOString(),
                endDate: moment().endOf("day").toISOString(),
            }),
        },
    },
    data() {
        return {
            colors: ["#FFBF00"],
            legends: [this.$t("report.sessionCount")],
            stats: {
                TopCountry: 0,
            },
            tableMode: false,
            loadingInstance: null,
            fullScreen: false,
            cancels: [],
            data: [],
            loading: true,
        };
    },
    computed: {},
    methods: {
        formatStat(value) {
            if (value === "-") {
                return this.$t("report.noEnoughtData");
            }
            return value;
        },
        fetch() {
            this.loading = true;
            this.loadingInstance.show();
            REPORT_API.websiteTrafficByCountry(this.filters)
                .then((res) => {
                    log("websiteTrafficByCountry data fetched", res);
                    this.data = res;
                })
                .finally(() => {
                    this.loading = false;
                });
        },
        switchToFullScreenMode() {
            if (this.fullScreen) {
                this.closeFullScreenMode();
            } else {
                const elem = document.querySelector(".website-traffic-by-country-chart");
                fscreen.requestFullscreen(elem);
                this.fullScreen = true;
            }
        },
        closeFullScreenMode() {
            fscreen.exitFullscreen();
        },
        getCols() {
            return [
                {
                    title: this.$t("message.country"),
                    dataIndex: "country",
                    key: "country",
                },
                {
                    title: this.$t("report.visitorCount"),
                    dataIndex: "newVisitorCount",
                    key: "newVisitorCount",
                },
            ];
        },
        getRows() {
            let items = this.data || [];

            log("getRows.items", items);
            const rows = items.map((item) => {
                return {
                    country: item.country,
                    newVisitorCount: item.newVisitorCount,
                };
            });
            return rows;
        },
        getStats() {
            let items = this.data || [];

            const TopCountry = _.chain(items).maxBy("newVisitorCount").value()?.country || "-";

            return {
                TopCountry,
            };
        },
        getLabels() {
            const countries = this.data || [];
            return countries.map((country) => country.country);
        },
        getDatasets() {
            const items = this.data || [];
            const palette = [
                "#06B6D4", "#3B82F6", "#10B981", "#F59E0B", "#EF4444",
                "#8B5CF6", "#EC4899", "#F97316", "#6366F1", "#14B8A6",
                "#84CC16", "#E11D48", "#0EA5E9", "#D946EF", "#78716C",
            ];
            return [
                {
                    label: this.$t("report.visitorCount"),
                    data: items.map((item) => item.newVisitorCount),
                    backgroundColor: items.map((_, i) => palette[i % palette.length] + "B3"),
                    borderColor: items.map((_, i) => palette[i % palette.length]),
                    borderWidth: 1,
                    borderRadius: 4,
                },
            ];
        },
        exportToExcel() {
            let items = this.data || [];

            log("exportToExcel.items", items);

            const excelData = items.map((item) => {
                return {
                    Country: item.country || "-",
                    "Total Visitor Count": item.newVisitorCount,
                };
            });
            expertToExcel(excelData, `website-traffic-by-country.xlsx`, "Sheet 1");
        },
    },
    mounted() {
        this.loadingInstance = this.$veLoading({
            target: document.querySelector(".website-traffic-by-country-chart"),
            name: "wave",
        });
        if (this.loading) {
            this.loadingInstance.show();
        }
        this.fetch();
    },
    created() {
        const fullscreenCallback = () => {
            console.log("fullscreenCallback", fscreen.fullscreenElement);
            if (fscreen.fullscreenElement !== null) {
                this.fullScreen = true;
            } else {
                this.fullScreen = false;
            }
            this.$forceUpdate();
        };
        fscreen.addEventListener("fullscreenchange", fullscreenCallback);
        this.cancels.push(() => {
            fscreen.removeEventListener("fullscreenchange", fullscreenCallback);
        });
    },
    beforeDestroy() {
        this.cancels.forEach((cancel) => cancel());
    },
    watch: {
        filters: {
            handler: function (val, oldVal) {
                this.fetch();
            },
            deep: true,
        },
        data: {
            handler: function (val, oldVal) {
                this.stats = this.getStats();
            },
            deep: true,
        },
        loading(val) {
            if (val) {
                this.loadingInstance.show();
            } else {
                this.loadingInstance.close();
            }
        },
    },
});
</script>
<style lang="scss">
.website-traffic-by-country-chart {
    &.fullscreen {
        background-color: #fff;
        padding: 20px;
        overflow-y: scroll;

        .stacked-area-chart {
            height: calc(100vh - 200px);
        }
    }
}
</style>
<style scoped lang="scss">
.website-traffic-by-country-chart {
    display: flex;
    flex-direction: column;
    gap: 10px;

    .toolbar {
        display: flex;
        justify-content: space-between;

        .left {
            display: flex;

            .buttons {
                display: flex;
                border: 1px solid var(--color-border-default);

                .icon-button {
                    padding: 5px;
                    width: 40px;
                    height: 34px;
                    display: flex;
                    justify-content: center;
                    align-items: center;
                    border-right: 1px solid var(--color-border-default);
                    border-radius: unset !important;

                    &.selected {
                        background-color: var(--color-bg-info-low-hover);

                        i {
                            color: rgb(55, 162, 255);
                        }
                    }

                    &:hover {
                        background-color: var(--color-bg-neutral-pressed);
                    }
                }

                .icon-button:last-child {
                    border-right: none;
                }
            }
        }

        .right {
            display: flex;
            justify-content: flex-end;
        }
    }

    .chart-wrapper {
        min-height: 300px;
        height: auto;
    }

    .stats {
        display: flex;
        flex-direction: row;
        gap: 10px;
        flex-wrap: wrap;
    }
}
</style>